import React from 'react';

/**
 * The agency's basic facts, for the (i) beside its name in the master header.
 *
 * WHY THIS EXISTS: the account page carried a second header — logo, name,
 * "Dept #7760" — directly under a master header that already said DGS,
 * Department of General Services and #7760. Measured on a 893px viewport, the
 * chrome above the first widget was 329px, 37% of the screen, with the account
 * name in three of six bands. The second header was 83px of that and told the
 * reader nothing the first one had not.
 *
 * Deleting it loses nothing. This puts back MORE than it carried, one click
 * away instead of permanently occupying the page.
 *
 * Every field is already on the `selectedDepartment` row the nav holds, so
 * opening this costs no request. Anything absent is simply omitted — a facts
 * card with "Employees: —" is worse than a shorter card, because a dash reads
 * as "we looked and there is none" when the truth is "this agency was never
 * enriched".
 */

const fmtSpend = (v) => {
  const n = Number(v) || 0;
  if (!n) return null;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
};

/** Strip a protocol and any trailing slash — the host is the useful part. */
const host = (url) => {
  if (!url) return null;
  try { return new URL(url).host.replace(/^www\./, ''); } catch { return String(url).slice(0, 40); }
};

export default function DepartmentFacts({ dept }) {
  if (!dept) return null;

  const mandate = dept.description || dept.mission_statement || dept.primary_function || null;
  const rows = [
    ['Dept', dept.department_number || dept.bu_code || dept.department_code],
    ['Employees', dept.employee_count_range],
    ['Budget', dept.annual_budget_range],
    ['Sites', dept.number_of_locations],
    ['Recorded spend', fmtSpend(dept.total_spend)],
    ['HQ', dept.headquarters_address],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== 0);

  return (
    <span className="dept-facts">
      {mandate && <span className="dept-facts-mandate">{mandate}</span>}
      {rows.length > 0 && (
        <span className="dept-facts-grid">
          {rows.map(([k, v]) => (
            <span className="dept-facts-row" key={k}>
              <span className="dept-facts-k">{k}</span>
              <span className="dept-facts-v">{v}</span>
            </span>
          ))}
        </span>
      )}
      {dept.main_website && (
        <span className="dept-facts-links">
          <a href={dept.main_website} target="_blank" rel="noopener noreferrer">
            {host(dept.main_website)}
          </a>
          {dept.procurement_website && (
            <a href={dept.procurement_website} target="_blank" rel="noopener noreferrer">
              procurement
            </a>
          )}
        </span>
      )}
    </span>
  );
}
